/**
 * Admin · Global Prompts list.
 *
 * Iter 27 — this page now shows ONLY ``scope='global'`` prompts.
 * Report- and client-scoped overrides used to land here too which
 * confused the admin mental model ("why does this say 'global
 * prompts' but show dashboard_explorer-specific overrides?"). The
 * per-report overrides are edited exclusively from the report
 * editor's AI tab → Training → AI Analysis prompt override.
 *
 * Existing report/client rows in the DB stay intact and the portal
 * still consults them via cascade; they're just no longer listed
 * here. A read-only summary at the bottom shows which reports have
 * active overrides, with deep-links to the AI tab.
 */

import { useEffect, useMemo, useState } from "react";
import { AlertCircle, ExternalLink, Info, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Badge, Button, Card, CardBody } from "@omnitok/ui";
import { Spinner } from "@pervasivemind/bi-components";

import { HttpError } from "@/lib/api";
import { draftsApi } from "@/lib/builder-api";
import {
  promptsApi,
  type PromptTemplate,
} from "@/lib/prompts-api";
import { useT } from "@/i18n";
import { PromptCreateModal } from "./PromptCreateModal";

export type OverrideTarget =
  | { kind: "report"; key: string }
  | { kind: "report-missing" }
  | { kind: "client" };

/** Where an override row links to. The editor route takes the report KEY; the row stores the draft UUID (#256). */
export function overrideTarget(
  row: PromptTemplate,
  draftKeyById: ReadonlyMap<string, string>,
): OverrideTarget {
  if (row.scope !== "report") return { kind: "client" };
  const key =
    row.report_draft_id === null
      ? undefined
      : draftKeyById.get(row.report_draft_id);
  return key === undefined ? { kind: "report-missing" } : { kind: "report", key };
}

export function PromptsPage(): JSX.Element {
  const { t } = useT();
  const navigate = useNavigate();
  const [allRows, setAllRows] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draftKeyById, setDraftKeyById] = useState<
    ReadonlyMap<string, string>
  >(new Map());

  async function reload(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      // Fetch ALL prompts in one go (no scope filter) so we can split
      // them into "global" (rendered as editable rows) and
      // "report/client" (rendered as read-only summary with deep
      // links). The portal-side cascade still resolves all three
      // scopes — we just don't expose the non-global ones for direct
      // editing from this page.
      const [res, drafts] = await Promise.all([
        promptsApi.list({}),
        draftsApi.list(),
      ]);
      setAllRows(res.items);
      setDraftKeyById(new Map(drafts.drafts.map((d) => [d.id, d.key])));
    } catch (err) {
      const message = err instanceof HttpError ? err.message : String(err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const { globalRows, overrideRows } = useMemo(() => {
    const g: PromptTemplate[] = [];
    const o: PromptTemplate[] = [];
    for (const row of allRows) {
      if (row.scope === "global") {
        g.push(row);
      } else {
        o.push(row);
      }
    }
    return { globalRows: g, overrideRows: o };
  }, [allRows]);

  return (
    // Rendered as a TAB inside Panorama IA — the page container,
    // title and tabs belong to `PanoramaIAPage`; this root matches
    // the sibling `IssuesQueue` (design unification 2026-07-17).
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-neutral-500">
          {t("admin.prompts.subtitleGlobal")}
        </p>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus size={14} />}
          onClick={() => setCreating(true)}
        >
          {t("admin.prompts.newBtnGlobal")}
        </Button>
      </div>

      {/* Iter 27 — info banner explains the mental model: globals
          here, overrides in each report's AI tab. */}
      <div className="flex items-start gap-2 rounded-lg border border-info/40 bg-info/10 px-4 py-2 text-sm text-info-dark">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span className="leading-relaxed">
          {t("admin.prompts.scopeBannerBodyIter27")}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-neutral-500">
          <Spinner size={16} ariaLabel={t("admin.common.loading")} />
          {t("admin.common.loading")}
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark">
          <AlertCircle size={14} /> {error}
        </div>
      ) : (
        <Card variant="bordered" noPadding>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-neutral-200 bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.prompts.col.name")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.prompts.col.version")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.prompts.col.active")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.prompts.col.updated")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {globalRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-8 text-center text-neutral-500"
                    >
                      {t("admin.prompts.empty")}
                    </td>
                  </tr>
                ) : (
                  globalRows.map((row) => (
                    <tr
                      key={row.id}
                      className="cursor-pointer transition-colors hover:bg-neutral-50"
                      onClick={() => navigate(`/admin/prompts/${row.id}`)}
                    >
                      <td className="px-4 py-3 font-mono text-xs">
                        <Link
                          to={`/admin/prompts/${row.id}`}
                          className="text-primary hover:underline"
                        >
                          {row.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-neutral-700">
                        v{row.version}
                      </td>
                      <td className="px-4 py-3">
                        {row.is_active ? (
                          <Badge variant="success" size="sm" pill>
                            {t("admin.prompts.col.active")}
                          </Badge>
                        ) : (
                          <span className="text-xs text-neutral-500">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-500">
                        {new Date(row.updated_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Iter 27 — read-only summary of existing overrides. The admin
          can't edit from here (intentional — they'd open the report
          editor's AI tab for that), but seeing which reports have an
          override is useful for compliance / inventory. */}
      {overrideRows.length > 0 && (
        <Card variant="bordered">
          <CardBody>
            <h2 className="text-sm font-semibold text-neutral-700">
              {t("admin.prompts.overridesTitle")}
            </h2>
            <p className="mt-1 text-xs text-neutral-500">
              {t("admin.prompts.overridesHint")}
            </p>
            <ul className="mt-3 space-y-1.5">
              {overrideRows.map((row) => {
                const target = overrideTarget(row, draftKeyById);
                const ref =
                  target.kind === "report"
                    ? target.key
                    : row.report_draft_id ?? row.client_key ?? "—";
                return (
                  <li
                    key={row.id}
                    className="flex items-center justify-between gap-2 text-xs"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="font-mono text-neutral-700">
                        {row.name}
                      </span>
                      <Badge
                        variant="neutral"
                        size="sm"
                        pill
                        className="ml-2"
                      >
                        {row.scope}
                      </Badge>
                      <span className="ml-2 font-mono text-xs text-neutral-500">
                        → {ref}
                      </span>
                    </span>
                    {target.kind === "report" ? (
                      <Link
                        to={`/builder/${encodeURIComponent(target.key)}?tab=ai`}
                        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-primary hover:underline"
                      >
                        <ExternalLink size={10} />
                        {t("admin.prompts.editInAiTab")}
                      </Link>
                    ) : target.kind === "report-missing" ? (
                      <span className="text-xs italic text-error-dark">
                        {t("admin.prompts.overrideDraftMissing")}
                      </span>
                    ) : (
                      <span className="text-xs italic text-neutral-500">
                        {t("admin.prompts.clientScopeReadonly")}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </CardBody>
        </Card>
      )}

      {creating ? (
        <PromptCreateModal
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            navigate(`/admin/prompts/${created.id}`);
          }}
        />
      ) : null}
    </div>
  );
}
